import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../config/api';
import { useTimer } from '../contexts/TimerContext';
import { useBooking } from '../contexts/BookingContext';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import { useSettings } from '../contexts/SettingsContext';
import MovieCard from '../components/MovieCard';
import TrailerModal from '../components/TrailerModal';
import './BookingPage.css';

/**
 * Extract film ID from filmCode (part after "HO")
 * Example: "1002HO00000363" -> "00000363"
 * Returns null if pattern doesn't match (fallback to exact match)
 */
const extractFilmId = (filmCode) => {
  if (!filmCode || typeof filmCode !== 'string') {
    return null;
  }
  
  // Pattern: {cinemaPrefix}HO{filmId}
  // Example: "1002HO00000363" -> extract "00000363"
  const match = filmCode.match(/HO(.+)$/);
  return match ? match[1] : null;
};

const parseAttributes = (attrString) => {
  const result = {};

  attrString
    .split('|')
    .filter(Boolean)
    .forEach(item => {
      const [key, value] = item.split('=');
      if (key && value) {
        result[key.toLowerCase()] = value;
      }
    });

  return result;
};

const BookingPage = () => {
  const navigate = useNavigate();
  const { movieId } = useParams();
  const { resetTimer } = useTimer();
  const { setSelectedSeats, clearBooking, setVistaTransactionId, setQuantity, quantity } = useBooking();
  const { getLocationForAPI, selectedCity, userLocation } = useLocationContext();
  const { getBookingSettings, instantRefresh } = useSettings();
  const [bookingSettings, setBookingSettings] = useState(null);
  const [movie, setMovie] = useState(null);
  const [loadingMovie, setLoadingMovie] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Check if we're coming back from seat selection with a preserved date
    const location = window.location;
    const urlParams = new URLSearchParams(location.search);
    const preservedDate = urlParams.get('date');
    
    if (preservedDate) {
      return preservedDate;
    }
    
    // Otherwise, default to today
    return new Date().toISOString().split('T')[0];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [allVistaShowGroups, setAllVistaShowGroups] = useState([]); // Store all shows (all dates)
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [pendingShowtime, setPendingShowtime] = useState(null);
  const [pendingMovie, setPendingMovie] = useState(null);
  const [pendingTheater, setPendingTheater] = useState(null);
  const [alsoShowingMovies, setAlsoShowingMovies] = useState([]);
  const [hasAutoSelectedDate, setHasAutoSelectedDate] = useState(false);
  const [showMovieInfo, setShowMovieInfo] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load booking settings from admin API
  const loadBookingSettings = useCallback(async () => {
    try {
      const settings = await getBookingSettings();
      setBookingSettings(settings);
      // console.log('Booking settings loaded:', settings);
    } catch (error) {
      console.error('Error loading booking settings:', error);
    }
  }, [getBookingSettings]);

  useEffect(() => {
    loadBookingSettings();
  }, [loadBookingSettings]);

  // Debug: Log when bookingSettings changes
  useEffect(() => {
    // console.log('BookingPage - bookingSettings changed:', bookingSettings);
    // console.log('BookingPage - maxSeatsPerBooking:', bookingSettings?.maxSeatsPerBooking);
  }, [bookingSettings]);

  // Listen for admin settings updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_settings_updated') {
        // console.log('Admin settings updated, refreshing booking settings...');
        loadBookingSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Also check for admin updates on page focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const lastAdminUpdate = localStorage.getItem('admin_settings_updated');
        const lastUpdate = localStorage.getItem('last_booking_settings_update');
        
        if (lastAdminUpdate && (!lastUpdate || parseInt(lastAdminUpdate) > parseInt(lastUpdate))) {
          // console.log('Page focused, admin settings were updated, refreshing booking settings...');
          loadBookingSettings();
          localStorage.setItem('last_booking_settings_update', Date.now().toString());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  // Fetch movie from API
  useEffect(() => {
    const fetchMovie = async () => {
      if (!movieId) return;
      
      try {
        setLoadingMovie(true);
        const movieData = await api.getMovieById(movieId);
        setMovie(movieData);
        // Reset auto-selection flag when movie changes
        setHasAutoSelectedDate(false);
      } catch (error) {
        console.error('Error fetching movie:', error);
        // If movie not found, redirect to home
        navigate('/');
      } finally {
        setLoadingMovie(false);
      }
    };

    fetchMovie();
  }, [movieId, navigate]);

  // Fetch all shows once when movie loads (not when date changes)
  useEffect(() => {
    const fetchVistaShows = async () => {
      if (!movie || !movie.vistaMovieId) {
        setAllVistaShowGroups([]);
        return;
      }

      try {
        setIsLoading(true);
        // Get location for API if available
        const locationForAPI = getLocationForAPI();
        const locationParams = locationForAPI
          ? {
              latitude: locationForAPI.lat,
              longitude: locationForAPI.lng,
            }
          : null;

        // Fetch all shows without date filter - API returns all dates
        // Pass null or empty string for date to get all shows
        const allShows = await api.getVistaShows(movie.vistaMovieId, null, null, locationParams);
        // Store all shows - we'll filter by date in the component
        setAllVistaShowGroups(Array.isArray(allShows) ? allShows : []);
      } catch (error) {
        console.error('Error fetching Vista shows:', error);
        setAllVistaShowGroups([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (movie) {
      fetchVistaShows();
    }
  }, [movie, selectedCity, userLocation]); // Fetch when movie or location changes

  // Fetch "Also Showing" movies (Now Showing movies excluding current movie)
  useEffect(() => {
    const fetchAlsoShowingMovies = async () => {
      try {
        // Fetch movies with status "Now Showing", limit to 6 (we'll filter out current movie and take 5)
        const movies = await api.getMovies({ status: 'Now Showing', limit: 6 });
        
        if (Array.isArray(movies)) {
          // Filter out the current movie and limit to 5
          const filteredMovies = movies
            .filter(m => m.id !== movieId && m.id !== movie?.id)
            .slice(0, 5);
          
          setAlsoShowingMovies(filteredMovies);
        } else {
          setAlsoShowingMovies([]);
        }
      } catch (error) {
        console.error('Error fetching also showing movies:', error);
        setAlsoShowingMovies([]);
      }
    };

    if (movieId || movie) {
      fetchAlsoShowingMovies();
    }
  }, [movieId, movie]);

  // Auto-select first future date with shows if today has no shows (only on initial load)
  useEffect(() => {
    // Only run once when shows are first loaded, and only if we haven't auto-selected before
    if (!allVistaShowGroups || allVistaShowGroups.length === 0 || !movie?.vistaMovieId || hasAutoSelectedDate) {
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    
    // Only auto-select if current selectedDate is today (user hasn't manually changed it)
    if (selectedDate !== today) {
      return;
    }

    // Extract film ID from movie's vistaMovieId for comparison
    const movieFilmId = movie.vistaMovieId ? extractFilmId(movie.vistaMovieId) : null;
    
    // Check if today has shows
    const todayHasShows = allVistaShowGroups.some((cinemaGroup) => {
      return (cinemaGroup.showtimes || []).some((showtime) => {
        const showFilmCode = showtime.filmCode || showtime.movieId;
        if (movie.vistaMovieId && showFilmCode) {
          // If we have a film ID extracted, match by film ID part (allows grouping across cinemas)
          if (movieFilmId) {
            const showFilmId = extractFilmId(showFilmCode);
            if (showFilmId !== movieFilmId) {
              return false; // Different movie (different film ID)
            }
          } else {
            // Fallback to exact match if extraction failed
            if (showFilmCode !== movie.vistaMovieId) {
              return false;
            }
          }
        }
        
        const showDate = showtime.date || showtime.datePart;
        if (showDate === today) {
          return true;
        }
        
        const showDateTime = showtime.showDateTime || showtime.startDateTime;
        if (showDateTime) {
          try {
            const dateObj = new Date(showDateTime);
            if (!Number.isNaN(dateObj.getTime())) {
              const showDateStr = dateObj.toISOString().split('T')[0];
              return showDateStr === today;
            }
          } catch (e) {
            // Ignore date parsing errors
          }
        }
        return false;
      });
    });

    // If today has no shows, find the first future date with shows
    if (!todayHasShows) {
      // Extract all unique dates from shows
      const allDates = new Set();
      
      // Extract film ID from movie's vistaMovieId for comparison
      const movieFilmId = movie.vistaMovieId ? extractFilmId(movie.vistaMovieId) : null;
      
      allVistaShowGroups.forEach((cinemaGroup) => {
        (cinemaGroup.showtimes || []).forEach((showtime) => {
          const showFilmCode = showtime.filmCode || showtime.movieId;
          if (movie.vistaMovieId && showFilmCode) {
            // If we have a film ID extracted, match by film ID part (allows grouping across cinemas)
            if (movieFilmId) {
              const showFilmId = extractFilmId(showFilmCode);
              if (showFilmId !== movieFilmId) {
                return; // Skip shows from other movies (different film ID)
              }
            } else {
              // Fallback to exact match if extraction failed
              if (showFilmCode !== movie.vistaMovieId) {
                return; // Skip shows from other movies
              }
            }
          }
          
          const showDate = showtime.date || showtime.datePart;
          if (showDate) {
            allDates.add(showDate);
          }
          
          const showDateTime = showtime.showDateTime || showtime.startDateTime;
          if (showDateTime) {
            try {
              const dateObj = new Date(showDateTime);
              if (!Number.isNaN(dateObj.getTime())) {
                const showDateStr = dateObj.toISOString().split('T')[0];
                allDates.add(showDateStr);
              }
            } catch (e) {
              // Ignore date parsing errors
            }
          }
        });
      });

      // Convert to array and sort
      const sortedDates = Array.from(allDates)
        .filter(date => date) // Remove empty dates
        .sort(); // Sort chronologically (YYYY-MM-DD format sorts correctly)

      // Find first future date (including today)
      const firstAvailableDate = sortedDates.find(date => date >= today);

      // If found a future date with shows, set it as selected
      if (firstAvailableDate && firstAvailableDate !== today) {
        setSelectedDate(firstAvailableDate);
        setHasAutoSelectedDate(true);
      }
    } else {
      // Today has shows, mark as checked so we don't run again
      setHasAutoSelectedDate(true);
    }
  }, [allVistaShowGroups, movie?.vistaMovieId, selectedDate, hasAutoSelectedDate]);

  // Filter shows by selected date and ensure they match the current movie (frontend filtering - no API call)
  const vistaShowGroups = useMemo(() => {
    if (!selectedDate || !allVistaShowGroups || allVistaShowGroups.length === 0 || !movie?.vistaMovieId) {
      return [];
    }

    // Extract film ID from movie's vistaMovieId for comparison
    const movieFilmId = extractFilmId(movie.vistaMovieId);

    // Filter shows for the selected date and matching filmCode/filmId
    return allVistaShowGroups.map((cinemaGroup) => {
      // Filter showtimes within each cinema group
      const filteredShowtimes = (cinemaGroup.showtimes || []).filter((showtime) => {
        // First check if filmCode/filmId matches (filter out shows from other movies)
        const showFilmCode = showtime.filmCode || showtime.movieId;
        if (movie.vistaMovieId && showFilmCode) {
          // If we have a film ID extracted, match by film ID part (allows grouping across cinemas)
          if (movieFilmId) {
            const showFilmId = extractFilmId(showFilmCode);
            if (showFilmId !== movieFilmId) {
              return false; // This show is for a different movie (different film ID)
            }
          } else {
            // Fallback to exact match if extraction failed
            if (showFilmCode !== movie.vistaMovieId) {
              return false; // This show is for a different movie
            }
          }
        }
        
        // Check if showtime date matches selected date
        const showDate = showtime.date || showtime.datePart;
        if (showDate === selectedDate) {
          return true;
        }
        
        // Also check showDateTime/startDateTime if date field is not available
        const showDateTime = showtime.showDateTime || showtime.startDateTime;
        if (showDateTime) {
          try {
            const dateObj = new Date(showDateTime);
            if (!Number.isNaN(dateObj.getTime())) {
              const showDateStr = dateObj.toISOString().split('T')[0];
              return showDateStr === selectedDate;
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
        
        return false;
      });

      // Return cinema group only if it has showtimes for the selected date
      if (filteredShowtimes.length === 0) {
        return null;
      }

      return {
        ...cinemaGroup,
        showtimes: filteredShowtimes,
      };
    }).filter(Boolean); // Remove null entries (cinemas with no shows for selected date)
  }, [allVistaShowGroups, selectedDate]);

  // Compute which dates have shows (for graying out dates without shows)
  const datesWithShows = useMemo(() => {
    if (!allVistaShowGroups || allVistaShowGroups.length === 0 || !movie?.vistaMovieId) {
      return new Set();
    }

    // Extract film ID from movie's vistaMovieId for comparison
    const movieFilmId = extractFilmId(movie.vistaMovieId);

    const datesSet = new Set();
    
    allVistaShowGroups.forEach((cinemaGroup) => {
      (cinemaGroup.showtimes || []).forEach((showtime) => {
        const showFilmCode = showtime.filmCode || showtime.movieId;
        if (movie.vistaMovieId && showFilmCode) {
          // If we have a film ID extracted, match by film ID part (allows grouping across cinemas)
          if (movieFilmId) {
            const showFilmId = extractFilmId(showFilmCode);
            if (showFilmId !== movieFilmId) {
              return; // Skip shows from other movies (different film ID)
            }
          } else {
            // Fallback to exact match if extraction failed
            if (showFilmCode !== movie.vistaMovieId) {
              return; // Skip shows from other movies
            }
          }
        }
        
        const showDate = showtime.date || showtime.datePart;
        if (showDate) {
          datesSet.add(showDate);
        }
        
        const showDateTime = showtime.showDateTime || showtime.startDateTime;
        if (showDateTime) {
          try {
            const dateObj = new Date(showDateTime);
            if (!Number.isNaN(dateObj.getTime())) {
              const showDateStr = dateObj.toISOString().split('T')[0];
              datesSet.add(showDateStr);
            }
          } catch (e) {
            // Ignore date parsing errors
          }
        }
      });
    });

    return datesSet;
  }, [allVistaShowGroups, movie?.vistaMovieId]);

  // When movieId changes, clear previous booking state and reset timer
  useEffect(() => {
    if (movieId) {
      // console.log('Movie ID changed to:', movieId);
      clearBooking();
      resetTimer();
      setSelectedSeats([]);
      setShowTrailer(false);
      // Reset to today's date
      setSelectedDate(new Date().toISOString().split('T')[0]);
    }
  }, [movieId, clearBooking, resetTimer, setSelectedSeats]);

  const getPosterImage = () => {
    if (!movie) return '/assets/img/movie-placeholder.png';
    return (
      movie.poster ||
      movie.banner ||
      movie.image ||
      '/assets/img/movie-placeholder.png'
    );
  };

  const formatReleaseDate = (dateValue) => {
    if (!dateValue) {
      return 'To Be Announced';
    }

    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }

    return dateValue;
  };

  const castList = React.useMemo(() => {
    if (!movie) return [];
    if (Array.isArray(movie.cast)) {
      return movie.cast.filter((member) => typeof member === 'string' && member.trim().length > 0);
    }
    if (typeof movie.cast === 'string' && movie.cast.trim().length > 0) {
      return movie.cast.split(',').map((member) => member.trim()).filter((member) => member.length > 0);
    }
    return [];
  }, [movie]);

  const displayCast = castList.length > 0 ? castList.join(', ') : 'Cast details coming soon';
  const displayDirector = movie?.director || 'Director information coming soon';
  const displayDuration = movie?.duration || 'Duration TBA';
  const displayGenre = movie?.genre
  ? String(movie.genre).replace(/[{}"]/g, '').split(',').map(g => g.trim()).filter(Boolean).join(', ')
  : 'Genre TBA';
  const displayLanguage = movie?.language || 'Language TBA';
  const displayRating = movie?.ageRating || movie?.rating || 'NR';
  const displayWarning = movie?.warning && movie.warning.trim().length > 0 ? movie.warning.trim() : null;
  const displaySynopsis = movie?.description?.trim() || 'Synopsis will be available shortly. Stay tuned!';
  const displayReleaseDate = formatReleaseDate(movie?.releaseDate);
  const posterImage = getPosterImage();


  // Show loading state while fetching movie
  if (loadingMovie) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading movie...</span>
        </div>
        <p className="mt-3">Loading movie details...</p>
      </div>
    );
  }

  // If movie not found, redirect to home
  if (!movie) {
    return null; // Will redirect in useEffect
  }

  // Generate dynamic dates starting from today
  // const generateDates = () => {
  //   const dates = [];
  //   const today = new Date();
    
  //   for (let i = 0; i < 5; i++) {
  //     const date = new Date(today);
  //     date.setDate(today.getDate() + i);
      
  //     const day = date.getDate().toString().padStart(2, '0');
  //     const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  //     const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
      
  //     dates.push({
  //       day,
  //       month,
  //       weekday,
  //       fullDate: date.toISOString().split('T')[0] // YYYY-MM-DD format
  //     });
  //   }
    
  //   return dates;
  // };

  const generateDates = () => {  //datefixtemp
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    // Get all dates that have shows, filter only future/today dates, sort them
    const futureDatesWithShows = Array.from(datesWithShows)
      .filter(date => date >= today.toISOString().split('T')[0])
      .sort();
  
    return futureDatesWithShows.map(fullDate => {
      const date = new Date(fullDate);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  
      return { day, month, weekday, fullDate };
    });
  };

  const dates = generateDates();

  const theaters = vistaShowGroups;

  // Check if showtime has already started
  const isShowtimeActive = (showtime) => {
    if (!showtime) return true;

    // Check if show is marked as elapsed or unavailable from backend
    if (showtime.isElapsed === true || showtime.available === false) {
      return false;
    }

    const now = new Date();
    const showDateTime = showtime.startDateTime ? new Date(showtime.startDateTime) : null;

    if (!showDateTime || Number.isNaN(showDateTime.getTime())) {
      return true;
    }

    const showDateIso = showDateTime.toISOString().split('T')[0];
    if (showDateIso !== selectedDate) {
      return showDateTime > now;
    }

    // Add booking timeout buffer - don't allow booking if show started more than booking timeout minutes ago
    const bookingTimeout = bookingSettings?.bookingTimeout || 15;
    const bufferTime = new Date(showDateTime.getTime() + bookingTimeout * 60000);
    return now < bufferTime;
  };

  // Get booking status for showtime
  const getBookingStatus = (showtime) => {
    if (!showtime) {
      return 'available';
    }

    // Check if show has elapsed (past show time)
    if (showtime.isElapsed === true || !isShowtimeActive(showtime)) {
      return 'elapsed';
    }

    if (showtime.available === false || showtime.availableSeats === 0) {
      return 'fully-booked';
    }

    if (
      typeof showtime.availableSeats === 'number' &&
      typeof showtime.totalSeats === 'number' &&
      showtime.totalSeats > 0
    ) {
      const soldSeats = showtime.totalSeats - showtime.availableSeats;
      // const fillPercentage = Math.max(0, Math.min(100, Math.round((soldSeats / showtime.totalSeats) * 100)));

      // if (fillPercentage >= 100) {
      //   return 'fully-booked';
      // }

      // if (fillPercentage >= 70) {
      //   return 'mostly-booked';
      // }
      let occupiedPercent = showtime.totalSeats > 0
      ? Math.max(0, Math.min(100, Math.round((soldSeats / showtime.totalSeats) * 100)))
      : 0;

      if (showtime.availableSeats === 0) return "sold-out";
      if (occupiedPercent >= 55) return "filling-fast";
    }

    return 'available';
  };

  const handleDateChange = (fullDate) => {
    // Reset timer and seat selection when changing date
    resetTimer();
    setSelectedSeats([]);
    // No need to set loading - filtering happens instantly on frontend
    setSelectedDate(fullDate);
    // Mark that user has manually changed the date, so don't auto-select anymore
    setHasAutoSelectedDate(true);
  };

  const handleShowtimeClick = async (theater, showtime) => {
    // Don't allow clicking on inactive shows
    if (!isShowtimeActive(showtime)) {
      return;
    }
    
    // Don't allow clicking on fully booked shows
    const bookingStatus = getBookingStatus(showtime);
    if (bookingStatus === 'fully-booked') {
      return;
    }
    
    // Check if quantity is selected
    const maxSeats = bookingSettings?.maxSeatsPerBooking || 8;
    if (!quantity || quantity < 1 || quantity > maxSeats) {
      // Show quantity selection modal
      setPendingShowtime(showtime);
      setPendingMovie(movie);
      setPendingTheater(theater);
      setShowQuantityModal(true);
      return;
    }
    
    // Proceed with booking initialization
    await proceedWithBooking(theater, showtime);
  };

  const proceedWithBooking = async (theater, showtime) => {
    // Reset timer and seat selection when switching shows
    resetTimer();
    setSelectedSeats([]);
    // Clear previous transaction ID when switching shows to prevent using wrong transaction ID
    setVistaTransactionId(null);
    
    // Initialize Vista booking before navigating to seat selection
    try {
      setIsLoading(true);
      
      // Get cinemaId and sessionId from showtime/theater
      // sessionId is the Vista session ID - it's stored in the showtime.id field (which is the Vista sessionId)
      const vistaCinemaId = theater.vistaCinemaId || theater.id;
      // The showtime.id is the Vista sessionId (set during show mapping)
      const sessionId = showtime.id || showtime.sessionId || showtime.vistaShowId;
      
      if (!vistaCinemaId || !sessionId) {
        console.error('Missing vistaCinemaId or sessionId for booking initialization');
        showError('Unable to initialize booking. Please try again.');
        setIsLoading(false);
        return;
      }
      
      // Initialize Vista booking
      const bookingData = await api.initVistaBooking(vistaCinemaId, sessionId);
      
      if (bookingData && bookingData.transactionId) {
        // Store Vista transaction ID and session ID together in context for validation
        setVistaTransactionId({
          transactionId: bookingData.transactionId,
          sessionId: sessionId, // Store the session ID this transaction belongs to
        });
        // console.log('Vista booking initialized', {
        //   transactionId: bookingData.transactionId,
        //   sessionId: sessionId,
        // });
    
    // Get the selected date object
    const selectedDateObj = dates.find(date => date.fullDate === selectedDate);
    
    navigate('/seat-selection', {
      state: {
        movie,
        showtime: {
          ...showtime,
          date: selectedDateObj ? `${selectedDateObj.weekday}, ${selectedDateObj.day} ${selectedDateObj.month}` : selectedDate,
          // Preserve original date/time fields for accurate booking
          originalDate: selectedDate, // Store ISO date format (YYYY-MM-DD)
          originalTime: showtime.time, // Store original time format
          // Preserve showDateTime/startDateTime if they exist
          showDateTime: showtime.showDateTime || showtime.startDateTime || null,
          startDateTime: showtime.startDateTime || showtime.showDateTime || null,
        },
        theater,
            selectedDate: selectedDate,
            vistaTransactionId: bookingData.transactionId, // Pass transaction ID in navigation state
            vistaSessionId: sessionId, // Pass session ID for validation
          }
        });
      } else {
        console.warn('Vista booking initialized but no transaction ID returned');
        showError('Failed to initialize booking. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error initializing Vista booking:', error);
      showError('Failed to initialize booking. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantitySelect = () => {
    const maxSeats = bookingSettings?.maxSeatsPerBooking || 8;
    if (quantity && quantity >= 1 && quantity <= maxSeats) {
      setShowQuantityModal(false);
      if (pendingShowtime && pendingTheater) {
        proceedWithBooking(pendingTheater, pendingShowtime);
      }
      setPendingShowtime(null);
      setPendingMovie(null);
      setPendingTheater(null);
    }
  };

  return (
    <>
      {/* Quick Navigation */}
      {/* <div className="quicknav">
        <div className="container">
          <ul>
            <li>
              <a href="#">Group Booking</a>
            </li>
            <li>
              <a href="#">Offers & Promotion</a>
            </li>
            <li>
              <a href="#">FAQ</a>
            </li>
            <li>
              <Link to="/contact-us">Contact Us</Link>
            </li>
          </ul>
        </div>
      </div> */}

      {/* Inner Page Header */}
      <div className="inner-head">
        <div className="container">
          <div className="inner-actions">
            {/* <a href="/" className="secondary-btn">
              Home
            </a> */}
            <ul className="stepper">
              <li className="active">
                <span></span>
                Select Cinema
              </li>
              <li>
                <span></span>
                Select Seat
              </li>
              <li>
                <span></span>
                F&B
              </li>
              <li>
                <span></span>
                Payment
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Movie Header with Poster, Title, Date Search and Showtimes */}
<div className="booking-summary">
  <div className="container">
    <div className="row">
      {/* Left Side - Poster and Title */}
      <div className="col-lg-4">
        <figure>
          <img src={posterImage} alt={movie.title} />
        </figure>
        {movie.trailer && (
          <button
            className="trailer-button"
            onClick={() => setShowTrailer(true)}
          >
            <i className="fas fa-play"></i>
            View Trailer
          </button>
        )}
      </div>

      {/* Right Side - Date Search and Showtimes */}
      <div className="col-lg-8">
      <h1 className="mt-3"> {movie.title.replace(/\(.*?\)/g, '').trim()}</h1>

        {/* Date Picker and Search */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-9">
            <div className="date-picker-wrapper">
              <div className="date-picker">
                {dates.map((date) => {
                  const hasShows = datesWithShows.has(date.fullDate);
                  return (
                    <div
                      key={date.fullDate}
                      className={`date-item ${
                        selectedDate === date.fullDate ? "active" : ""
                      } ${!hasShows ? "no-shows" : ""}`}
                      onClick={() => {
                        if (hasShows) {
                          handleDateChange(date.fullDate);
                        }
                      }}
                    >
                      <span className="month">{date.month}</span>
                      <span className="day">{date.day}</span>
                      <span className="weekday">{date.weekday}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* <div className="col-md-3">
            <div className="filter-group">
              <select 
                className="form-control th-input filter-select"
                value={quantity || ''}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              >
                <option value="">Select Qty</option>
                {Array.from({ length: (bookingSettings?.maxSeatsPerBooking || 8) }, (_, i) => i + 1).map((qty) => (
                  <option key={qty} value={qty}>{qty}</option>
                ))}
              </select>
            </div>
          </div> */}
        </div>


        {/* Showtimes */}
        <div className="row">
          <div className="col-12">
            {isLoading ? (
              <div className="skeleton-loading">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="skeleton-theater-card">
                    <div className="skeleton-theater-info">
                      <div className="skeleton-theater-name"></div>
                      <div className="skeleton-theater-location"></div>
                    </div>
                    <div className="skeleton-showtimes">
                      <div className="skeleton-showtime"></div>
                      <div className="skeleton-showtime"></div>
                      <div className="skeleton-showtime"></div>
                      <div className="skeleton-showtime"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : theaters && theaters.length > 0 ? (
              theaters.map((theater, index) => {
                const theaterId =
                  theater.id ||
                  theater.cinemaId ||
                  theater.vistaCinemaId ||
                  `theater-${index}`;
                const theaterName =
                  theater.name || theater.cinemaName || `Cinema ${index + 1}`;
                const theaterLocation = theater.location || theater.city || "";
                const vistaCinemaId = theater.vistaCinemaId;
                const showList = Array.isArray(theater.showtimes)
                  ? theater.showtimes
                  : Array.isArray(theater.shows)
                  ? theater.shows
                  : [];

                return (
                  <div key={theaterId} className="theater-card">
                    <div className="theater-info">
                      <h3 className="theater-name">{theaterName}</h3>
                      {theaterLocation && (
                        <p className="theater-location">{theaterLocation}</p>
                      )}
                    </div>
                    <div className="show-times">
                      {showList.length > 0 ? (
                        showList.map((show) => {
                          const isActive = isShowtimeActive(show);
                          const bookingStatus = getBookingStatus(show);
                          const canClick =
                            isActive && bookingStatus !== "fully-booked";

                          return (
                            <div
                              key={show.id || `${theaterId}-${show.time}`}
                              className={`show-time-item show-time-${bookingStatus} ${
                                !isActive ? "inactive" : ""
                              }`}
                              onClick={() =>
                                handleShowtimeClick(
                                  {
                                    id: theaterId,
                                    name: theaterName,
                                    location: theaterLocation,
                                    city: theater.city,
                                    vistaCinemaId,
                                  },
                                  show
                                )
                              }
                              style={{
                                cursor: canClick ? "pointer" : "not-allowed",
                              }}
                            >
                              <div className="show-time-border"></div>
                              <div className="show-time-content">
                                <span className="show-time">{show.time}</span>
                                {/* <span className="show-type">
                                  {show.experience || show.screenType || ""}
                                </span>*/}
                                {show.screenName && (
                                  <span className="show-screen">
                                    {show.screenName}
                                  </span>
                                )} 
                                {show.screenAttributes && (
                                  <span className="show-screen-attributes">
                                    {parseAttributes(show.screenAttributes)?.screenattributes || "asdsad"}
                                  </span>
                                )}
                                {/* {show.availableSeats !== null &&
                                  show.availableSeats !== undefined &&
                                  typeof show.availableSeats === "number" && (
                                    <span className="show-seats">{`${show.availableSeats} seats left`}</span>
                                  )} */}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-showtimes">
                          <p>No showtimes available for this cinema.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-theaters">
                <p>
                  {movie.status === 'Coming Soon' 
                    ? <span className="coming-soon">Coming Soon - Tickets will be available soon!</span> 
                    : 'No theaters available for this movie.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

{/* Film Details Section */}
<div className="film-details">
  <div className="container">

    {/* Mobile Toggle Button */}
    <div className="mobile-movie-info-toggle d-md-none">
      <button
        className="movie-info-btn"
        onClick={() => setShowMovieInfo(!showMovieInfo)}
      >
        {showMovieInfo ? "Hide Movie Info ▲" : "Read Movie Info ▼"}
      </button>
    </div>

    {/* Movie Info Content */}
    <div className={`movie-info-content ${showMovieInfo ? "show" : ""}`}>
      <div className="row">
        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Cast</div>
          <div className="value">{displayCast}</div>
        </div>

        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Release</div>
          <div className="value">{displayReleaseDate}</div>
        </div>

        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Direction</div>
          <div className="value">{displayDirector}</div>
        </div>

        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Running Time</div>
          <div className="value">{displayDuration}</div>
        </div>

        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Genre</div>
          <div className="value">{displayGenre}</div>
        </div>

        <div className="col-md-6 col-lg-4 mb-30">
          <div className="key">Language</div>
          <div className="value">{displayLanguage}</div>
        </div>

        <div className="col-md-12 col-lg-8 mb-30">
          <div className="key">Censor Rating</div>
          <div className="value censor">
            <span className="age-rating">{displayRating}</span>
            {displayWarning && (
              <span className="censor-rating">{displayWarning}</span>
            )}
          </div>
        </div>

        <div className="col-12 mb-30">
          <div className="key">Synopsis</div>
          <div className="value">{displaySynopsis}</div>
        </div>
      </div>
    </div>

  </div>
</div>

     {/* Movie area */}
<div className="movie-area movie-bg">
  <div className="container">
    <div className="tab-content">
      <div className="tab-pane fade show active" role="tabpanel">
        <div className="col-md-12 mb-3">
          <h4>Also Showing</h4>
        </div>
        <div className="mc-scroll">
          {alsoShowingMovies.map((movie) => (
            <div key={movie.id}>
              <MovieCard movie={movie} showBookButton={true} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
      </div>

      {/* FAB Button */}
      {/* <div className="fab-btn">
        <a href="#" className="primary-btn help-btn">
          <img src="/assets/img/icons/help.svg" alt="Help" /> Help
        </a>
        <a href="#" className="primary-btn fab-mob pulse">
          <i className="fas fa-ticket"></i>
        </a>
      </div> */}

      {/* Trailer Modal */}
      <TrailerModal
        isOpen={showTrailer}
        onClose={() => setShowTrailer(false)}
        trailerUrl={movie.trailer}
      />

      {/* Quantity Selection Modal */}
      {showQuantityModal && (
        <div className="modal-overlay" onClick={() => {
          setShowQuantityModal(false);
          setPendingShowtime(null);
          setPendingMovie(null);
          setPendingTheater(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Select Quantity</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                  setPendingTheater(null);
                }}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>Please select the number of tickets before proceeding.</p>
              <div className="form-group">
                <label htmlFor="modal-quantity">Select Qty</label>
                <select
                  id="modal-quantity"
                  className="form-control"
                  value={quantity || ''}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                >
                  <option value="">Select Qty</option>
                  {Array.from({ length: (bookingSettings?.maxSeatsPerBooking || 8) }, (_, i) => i + 1).map((qty) => (
                    <option key={qty} value={qty}>{qty}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                  setPendingTheater(null);
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleQuantitySelect}
                disabled={!quantity || quantity < 1 || quantity > (bookingSettings?.maxSeatsPerBooking || 8)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">
              {errorMessage}
            </div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingPage;
